import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { useOrg } from "../contexts/OrgProvider";
import { FRAMEWORKS } from "../constants/frameworks";
import EngagementsPanel from "../components/EngagementsPanel";
import { TableSkeleton } from "../components/skeletons";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileCheck, Search, Filter, CheckCircle2, XCircle, Clock, AlertCircle,
  FileText, Link2, RefreshCw, Download, ChevronDown
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { StatusBadge } from "../components/ui/status-badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { Progress } from "../components/ui/progress";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from "../components/ui/dialog";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger
} from "../components/ui/collapsible";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const SoA = () => {
  const { token } = useAuth();
  const { t, locale } = useI18n();
  const [soaEntries, setSoaEntries] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFramework, setSelectedFramework] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [editingEntry, setEditingEntry] = useState(null);
  const [summary, setSummary] = useState([]);

  const { activeFrameworks } = useOrg();
  const frameworks = FRAMEWORKS
    .filter((f) => activeFrameworks.includes(f.id))
    .map((f) => ({ value: f.id, label: f.short, color: f.color }));

  const implementationStatuses = [
    { value: "not_started", label: t("not_started") },
    { value: "in_progress", label: t("in_progress") },
    { value: "implemented", label: t("implemented") },
    { value: "not_applicable", label: t("not_applicable") }
  ];

  useEffect(() => {
    fetchData();
  }, [token, locale]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (locale) params.set("locale", locale);
      const soaParams = params.toString() ? `?${params.toString()}` : "";
      const [soaRes, docsRes, evidenceRes, summaryRes] = await Promise.all([
        axios.get(`${API}/soa${soaParams}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/documents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/evidence`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/soa/summary`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSoaEntries(soaRes.data);
      setDocuments(docsRes.data);
      setEvidence(evidenceRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error("Failed to fetch SoA data:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateSoA = async (framework) => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/soa/generate?framework=${framework}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(t("soa.toast_generate_failed"));
    } finally {
      setGenerating(false);
    }
  };

  const updateEntry = async () => {
    if (!editingEntry) return;
    try {
      await axios.put(`${API}/soa/${editingEntry.soa_id}`, {
        control_id: editingEntry.control_id,
        applicable: editingEntry.applicable,
        justification: editingEntry.justification,
        implementation_status: editingEntry.implementation_status,
        responsible: editingEntry.responsible,
        notes: editingEntry.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("soa.toast_entry_updated"));
      setEditingEntry(null);
      fetchData();
    } catch (error) {
      toast.error(t("soa.toast_update_failed"));
    }
  };

  const getFrameworkColor = (framework) => {
    return frameworks.find(f => f.value === framework)?.color || "#6B7280";
  };

  const filteredEntries = soaEntries.filter(entry => {
    const matchesSearch = entry.control_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.control_number?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFramework = selectedFramework === "all" || entry.framework === selectedFramework;
    const matchesStatus = selectedStatus === "all" || entry.implementation_status === selectedStatus;
    return matchesSearch && matchesFramework && matchesStatus;
  });

  // Group by framework
  const groupedEntries = filteredEntries.reduce((acc, entry) => {
    if (!acc[entry.framework]) {
      acc[entry.framework] = [];
    }
    acc[entry.framework].push(entry);
    return acc;
  }, {});

  const calculateProgress = (framework) => {
    const entries = soaEntries.filter(e => e.framework === framework);
    if (entries.length === 0) return 0;
    const implemented = entries.filter(e => e.implementation_status === "implemented").length;
    return Math.round((implemented / entries.length) * 100);
  };

  if (loading) {
    return <TableSkeleton rows={8} columns={5} label={t("soa.loading")} />;
  }

  return (
    <div className="p-6 space-y-6" data-testid="soa-page">
      <PageHeader
        title={t("soa.title")}
        description={t("soa.subtitle")}
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="w-48 justify-between"
                disabled={generating}
                data-testid="generate-soa-select"
              >
                {generating ? t("generating") : t("soa.generate")}
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {frameworks.map(fw => (
                <DropdownMenuItem key={fw.value} onClick={() => generateSoA(fw.value)}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t("soa.generate")} {fw.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      {/* Client Engagements Panel */}
      <EngagementsPanel
        assessmentType="soa"
        runEndpoint="run-soa"
        titleKey="soa.assessments"
        newButtonKey="soa.new_assessment"
      />

      {/* Framework Progress Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {frameworks.map(fw => {
          const progress = calculateProgress(fw.value);
          const count = soaEntries.filter(e => e.framework === fw.value).length;
          return (
            <Card
              key={fw.value}
              className={`cursor-pointer transition-all ${selectedFramework === fw.value ? 'ring-2 ring-primary' : 'hover:border-primary/50'}`}
              onClick={() => setSelectedFramework(selectedFramework === fw.value ? "all" : fw.value)}
              data-testid={`soa-framework-${fw.value}`}
            >
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: fw.color }} />
                  <span className="font-medium text-sm">{fw.label}</span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{count} {t("wizard.controls_label")}</span>
                    <span className="font-semibold">{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t("search") + "..."}
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="soa-search-input"
          />
        </div>
        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger className="w-full md:w-44" data-testid="soa-status-filter">
            <SelectValue placeholder={t("status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("soa.all_statuses")}</SelectItem>
            {implementationStatuses.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Results Count */}
      <p className="text-sm text-muted-foreground">
        {t("soa.showing_of_entries").replace("{x}", filteredEntries.length).replace("{y}", soaEntries.length)}
      </p>

      {/* SoA Entries by Framework */}
      <ScrollArea className="h-[calc(100vh-28rem)]">
        <div className="space-y-4 pr-4">
          {Object.keys(groupedEntries).length === 0 ? (
            <div className="text-center py-12">
              <FileCheck className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">{t("soa.no_entries")}</p>
              <p className="text-sm text-muted-foreground mt-2">
                {t("soa.no_entries_hint")}
              </p>
            </div>
          ) : (
            Object.entries(groupedEntries).map(([framework, entries]) => (
              <Collapsible key={framework} defaultOpen>
                <Card className="border-border">
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getFrameworkColor(framework) }} />
                          <CardTitle className="text-lg">
                            {frameworks.find(f => f.value === framework)?.label || framework}
                          </CardTitle>
                          <Badge variant="secondary">{entries.length} {t("wizard.controls_label")}</Badge>
                        </div>
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="space-y-2">
                        {entries.map((entry) => (
                          <div
                            key={entry.soa_id}
                            className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors"
                            onClick={() => setEditingEntry(entry)}
                            data-testid={`soa-entry-${entry.soa_id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-mono text-sm text-muted-foreground">
                                  {entry.control_number}
                                </span>
                                <StatusBadge kind="impl" value={entry.implementation_status} />
                                {!entry.applicable && (
                                  <Badge variant="secondary">N/A</Badge>
                                )}
                              </div>
                              <p className="text-sm font-medium truncate">{entry.control_title}</p>
                              {entry.responsible && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {t("owner")}: {entry.responsible}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              {entry.linked_documents?.length > 0 && (
                                <Badge variant="outline" className="text-xs">
                                  <FileText className="w-3 h-3 mr-1" />
                                  {entry.linked_documents.length}
                                </Badge>
                              )}
                              {entry.linked_evidence?.length > 0 && (
                                <Badge variant="outline" className="text-xs">
                                  <Link2 className="w-3 h-3 mr-1" />
                                  {entry.linked_evidence.length}
                                </Badge>
                              )}
                              {entry.mapped_controls?.length > 0 && (
                                <Badge variant="outline" className="text-xs bg-purple-100 dark:bg-purple-900/30">
                                  {entry.mapped_controls.length} {t("soa.mapped")}
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))
          )}
        </div>
      </ScrollArea>

      {/* Edit Entry Dialog */}
      <Dialog open={!!editingEntry} onOpenChange={() => setEditingEntry(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("soa.edit_entry")}</DialogTitle>
            <DialogDescription>
              {editingEntry?.control_number} - {editingEntry?.control_title}
            </DialogDescription>
          </DialogHeader>
          {editingEntry && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <Label className="w-24">{t("soa.applicable")}</Label>
                <Select
                  value={editingEntry.applicable ? "yes" : "no"}
                  onValueChange={(v) => setEditingEntry({ ...editingEntry, applicable: v === "yes" })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">{t("yes")}</SelectItem>
                    <SelectItem value="no">{t("no")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("soa.implementation")}</Label>
                <Select
                  value={editingEntry.implementation_status}
                  onValueChange={(v) => setEditingEntry({ ...editingEntry, implementation_status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {implementationStatuses.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("soa.responsible")}</Label>
                <Input
                  value={editingEntry.responsible || ""}
                  onChange={(e) => setEditingEntry({ ...editingEntry, responsible: e.target.value })}
                  placeholder={t("soa.responsible_placeholder")}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("soa.justification")}</Label>
                <Textarea
                  value={editingEntry.justification || ""}
                  onChange={(e) => setEditingEntry({ ...editingEntry, justification: e.target.value })}
                  placeholder={t("soa.justification_placeholder")}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("notes")}</Label>
                <Textarea
                  value={editingEntry.notes || ""}
                  onChange={(e) => setEditingEntry({ ...editingEntry, notes: e.target.value })}
                  placeholder={t("soa.notes_placeholder")}
                />
              </div>

              {/* Mapped Controls */}
              {editingEntry.mapped_controls?.length > 0 && (
                <div className="space-y-2">
                  <Label>{t("soa.cross_framework_mappings")}</Label>
                  <div className="space-y-1">
                    {editingEntry.mapped_controls.map((mapped, idx) => (
                      <div key={idx} className="text-sm p-2 rounded bg-muted/50">
                        <Badge variant="outline" className="mr-2">
                          {frameworks.find(f => f.value === mapped.framework)?.label}
                        </Badge>
                        {mapped.control_number} - {mapped.title}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingEntry(null)}>{t("cancel")}</Button>
            <Button onClick={updateEntry}>{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SoA;
